import { FC, useMemo } from 'react';

import { DisposalRecordFloorUnit } from '@agency/apis/disposals';
import { useAgencyConstants, useConfig } from '@core/contexts/ConfigContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Flex } from '@shared/components/flex';
import { MultiGroupSelect } from '@shared/components/group-select-new/multi-group-select';
import { NumberField } from '@shared/components/number-field';
import { RadioGroup, RadioGroupOption } from '@shared/components/radio-group';
import { Option } from '@shared/components/select-new/Select';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { useCoreOptions } from '@shared/contexts/OptionsContext';
import { ButtonSize, NewButtonColour } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';
import { groupOptionsByBuildingType } from '@shared/utils/options';

import { RecordSpaceItem } from './components/record-space-item';
import { styles } from './RecordSpacesTakenForm.styles';
import { Button } from '../button-new';

export interface RecordSpaceItemError {
  description: string;
  floor: string;
  size: string;
}

const getEntirePropertyRadioOptions = (): RadioGroupOption[] => [
  {
    id: 1,
    label: t('entire_property'),
  },
  {
    id: 0,
    label: t('partial'),
  },
];

export interface RecordSpacesTakenFormProps extends WithStyles<typeof styles> {
  buildingTypes?: string[];
  buildingTypesError?: boolean;
  disabled?: boolean;
  entireProperty?: number;
  fitoutConditionOptions?: Option[];
  floorUnits: DisposalRecordFloorUnit[];
  sizeMeasure?: string;
  ourDisposal?: boolean;
  propertyAddress?: string;
  propertyId: number;
  sizeError: boolean;
  sizeMetric: Metric;
  sizeValue: string;
  spaceCardErrors: Record<number, RecordSpaceItemError>;
  onEditFloorUnit: (floorUnit: Partial<DisposalRecordFloorUnit>, idx: number) => void;
  onAddFloorUnit: () => void;
  onChangeBuildingTypes: (buildingTypes: string[]) => void;
  onChangeEntireProperty: (value: number) => void;
  onChangeMeasureType?: (sizeMeasure: string) => void;
  onChangeSizeMetric: (sizeMetric: Metric, floor?: boolean) => void;
  onChangeSizeValue: (sizeValue: string) => void;
  onEditAddress?: () => void;
}

const RecordSpacesTakenFormComponent: FC<RecordSpacesTakenFormProps> = ({
  buildingTypes = [],
  buildingTypesError = false,
  classes,
  disabled = false,
  entireProperty = 1,
  fitoutConditionOptions = [],
  floorUnits,
  sizeMeasure,
  ourDisposal = false,
  propertyAddress,
  propertyId,
  sizeMetric,
  sizeError,
  spaceCardErrors,
  sizeValue,
  onEditFloorUnit,
  onAddFloorUnit,
  onChangeBuildingTypes,
  onChangeEntireProperty,
  onChangeMeasureType,
  onChangeSizeMetric,
  onChangeSizeValue,
  onEditAddress,
}) => {
  const { constants } = useAgencyConstants();
  const { user } = useConfig();
  const { availableSizeMetricOptions } = useCoreOptions();

  const buildingTypeGroupOptions = useMemo(() => groupOptionsByBuildingType(constants, user), [constants, user]);

  const measuresList = React.useMemo(() => {
    if (constants.availableMeasures) {
      const measures = constants.availableMeasures;

      return Object.keys(measures).map((el) => {
        return { label: measures[el], id: el } as Option;
      });
    }
    return [];
  }, [constants]);

  const entirePropertyRadioOptions = useMemo(() => getEntirePropertyRadioOptions(), []);

  return (
    <div className={classes.root}>
      <Flex justifyContent="space-between" className={classes.cardHeader}>
        <span className={classes.cardTitle}>{t('property_and_space')}</span>
      </Flex>
      <div className={classes.addressContainer}>
        <TextField
          classes={{ root: classes.addressField }}
          disabled
          fullWidth
          size={InputSize.medium}
          value={propertyAddress || t('no_address_provided')}
          onChange={() => {
            return;
          }}
        />
        {onEditAddress && (
          <Button
            className={classes.editButton}
            colour={NewButtonColour.Outline}
            size={ButtonSize.medium}
            onClick={onEditAddress}
          >
            {t('edit_address')}
          </Button>
        )}
      </div>
      <div className={classes.divider} />
      <div className={classes.buildingTypesRow}>
        <span className={classes.label}>{t('what_is_the_property_type')}</span>
        <MultiGroupSelect
          classes={{ labelRoot: buildingTypesError ? classes.subLabelError : classes.subLabel }}
          error={buildingTypesError}
          fullWidth
          placeholder={t('xtextx_ellipsis', { text: t('select_an_option') })}
          size={ButtonSize.medium}
          value={buildingTypes}
          groupOptions={buildingTypeGroupOptions}
          onChange={(value) => onChangeBuildingTypes(value as string[])}
        />
      </div>

      <div className={classes.entirePropertySection}>
        <span className={classes.label}>{t('did_they_take_the_entire_property_question')}</span>

        <RadioGroup
          classes={{ root: classes.entirePropertyRadioGroup }}
          label={null}
          options={entirePropertyRadioOptions}
          value={entireProperty}
          direction="row"
          onChange={(value) => onChangeEntireProperty(value as number)}
        />
      </div>
      <div
        className={classes.floorUnitsLabel}
        dangerouslySetInnerHTML={{
          __html: ourDisposal
            ? t('floor_units_taken_bracket_note_let_or_sold_floor_units_are_not_shown_bracket')
            : t('floor_units_taken'),
        }}
      />
      {floorUnits.map((floorUnit, idx) => (
        <RecordSpaceItem
          classes={{ metricSelect: classes.metricSelect }}
          disabled={disabled}
          entireProperty={entireProperty}
          key={`${floorUnit.id}${idx}`}
          card={floorUnit}
          errorValidation={spaceCardErrors[idx]}
          fitoutConditionOptions={fitoutConditionOptions}
          propertyId={propertyId}
          onSizeMetricChange={(metric) => onChangeSizeMetric(metric, true)}
          editCard={(update) => onEditFloorUnit(update, idx)}
        />
      ))}
      <div className={classes.divider} />
      <div className={classes.formFooter}>
        <span className={classes.addFloorUnitButton} onClick={onAddFloorUnit}>
          {t('plus_xtextx', { text: t('floorunit') })}
        </span>
        <div className={classes.totalSpace}>
          <span className={classes.label}>{t('xlabelx_colon', { label: t('total_size_1') })}</span>
          <NumberField
            classes={{ root: classes.totalSizeField }}
            error={sizeError}
            min={0}
            placeholder={t('enter_size')}
            size={InputSize.medium}
            value={sizeValue}
            onChange={onChangeSizeValue}
          />
          <SingleSelect
            classes={{ root: classes.metricSelect }}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={sizeMetric}
            options={availableSizeMetricOptions}
            onChange={(value) => onChangeSizeMetric(value as Metric)}
          />
          {onChangeMeasureType && (
            <SingleSelect
              classes={{ root: classes.metricSelect }}
              placeholder={t('xtextx_ellipsis', { text: t('measurement_type') })}
              size={ButtonSize.medium}
              value={sizeMeasure}
              options={measuresList}
              onChange={(value) => onChangeMeasureType(value as string)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const RecordSpacesTakenForm = withStyles(styles)(RecordSpacesTakenFormComponent);
